import React, { useEffect, useMemo, useRef, useState } from 'react'
import IntlMessages from '@crema/utility/IntlMessages'
import Alert from '@material-ui/lab/Alert'
import Box from '@material-ui/core/Box'
import Grid from '@material-ui/core/Grid'
import GoBack from 'components/GoBack'
import { RouteConfigComponentProps } from 'react-router-config'
import { useAppDispatch, useAppSelector } from 'store/hooks'
import {
    offeringTagCreateThunk,
    offeringTagDeleteThunk,
    offeringTagListThunk,
    offeringTagRetrieveThunk,
    offeringTagUpdateThunk,
    selectCurrentTempleOfferingTags,
    selectOfferingTagById,
    selectOfferingTagStatus,
    selectAvailableTags,
    tagListThunk
} from 'store/reducers/temple/offeringTagSlice'
import FitContentPaper from 'components/Paper/FitContentPaper'
import FormOfferingTag, { OfferingTagFormValues } from './FormOfferingTag'
import BulkProgressList, { BulkOperationProgress } from './BulkProgressList'
import { FormikHelpers } from 'formik'
import { unwrapResult } from '@reduxjs/toolkit'
import { extractError, getFieldErrors } from 'utils/api'
import useRestricter from 'hooks/useRestricter'
import { PERMISSIONS, RESTRICTED_PATHS } from 'utils/permissions'
import { offeringListThunk, selectCurrentTempleOfferings } from 'store/reducers/temple/offeringSlice'

interface RouteParams {
    id?: string
    name?: string
}

const normalizeTagName = (name?: string) => (name ?? '').trim().toLowerCase()

const isSameOffering = (tagOffering: string | number, selectedOffering: string | number) =>
    tagOffering === selectedOffering || String(tagOffering) === String(selectedOffering)

const EditOfferingTag: React.FC<RouteConfigComponentProps<RouteParams>> = (props) => {
    const { id, name } = props.match.params
    const groupName = name ? decodeURIComponent(name) : ''
    const api = useAppSelector(selectOfferingTagStatus)
    const offeringTag = useAppSelector(state => selectOfferingTagById(state, id ? id : ''))
    const offeringTags = useAppSelector(selectCurrentTempleOfferingTags)
    const offerings = useAppSelector(selectCurrentTempleOfferings)
    const availableTags = useAppSelector(selectAvailableTags)
    const [isSuccess, setIsSuccess] = useState(false)
    const [submitError, setSubmitError] = useState('')
    const [progress, setProgress] = useState<BulkOperationProgress[] | null>(null)
    const [initialValues, setInitialValues] = useState<OfferingTagFormValues>({ offering: '', offerings: [], tag: '', name: '' })
    const didFetchRef = useRef(false)
    const dispatch = useAppDispatch()
    const hasEditPermission = useRestricter(PERMISSIONS.CHANGE, RESTRICTED_PATHS.OFFERING_TAGS)

    const groupTags = useMemo(() => {
        if (groupName) {
            return offeringTags.filter(tag => normalizeTagName(tag.tagName ?? tag.name) === groupName)
        }
        return offeringTag ? [offeringTag] : []
    }, [groupName, offeringTag, offeringTags])

    useEffect(() => {
        if (didFetchRef.current) return
        didFetchRef.current = true

        dispatch(offeringTagListThunk({}))
        dispatch(tagListThunk({ tag_type: 'offering' }))
        if (offerings.length === 0) {
            dispatch(offeringListThunk({}))
        }
        if (!groupName && id) {
            dispatch(offeringTagRetrieveThunk({ id }))
        }
    }, [dispatch, groupName, id, offerings.length])

    useEffect(() => {
        if (groupTags.length > 0) {
            setInitialValues({
                offering: groupTags[0].offering,
                offerings: groupTags.map(tag => tag.offering),
                name: groupTags[0].tagName ?? groupTags[0].name ?? '',
                tag: groupTags[0].tag
            })
        }
    }, [groupTags])

    const getOfferingName = (offeringRef: string | number) => {
        const found = offerings.find(o => o.url === offeringRef || String(o.id) === String(offeringRef))
        return found ? found.name : String(offeringRef)
    }

    const handleSubmit = async (data: OfferingTagFormValues, actions: FormikHelpers<OfferingTagFormValues>) => {
        setIsSuccess(false)
        setSubmitError('')
        setProgress(null)

        const currentGroupTags = groupTags
        const retainedTags = currentGroupTags.filter(tag =>
            data.offerings.some(offering => isSameOffering(tag.offering, offering))
        )
        const removedTags = currentGroupTags.filter(tag =>
            !data.offerings.some(offering => isSameOffering(tag.offering, offering))
        )
        const addedOfferings = data.offerings.filter(offering =>
            !currentGroupTags.some(tag => isSameOffering(tag.offering, offering))
        )

        // Build list of operations to execute
        const operations: {
            offeringId: string | number
            offeringName: string
            operation: 'create' | 'update' | 'delete'
            run: () => Promise<any>
        }[] = []

        removedTags.forEach(tag => {
            operations.push({
                offeringId: tag.offering,
                offeringName: getOfferingName(tag.offering),
                operation: 'delete',
                run: () => dispatch(offeringTagDeleteThunk(tag)).then(unwrapResult)
            })
        })

        retainedTags
            .filter(tag => (tag.tagName ?? tag.name) !== data.name)
            .forEach(tag => {
                operations.push({
                    offeringId: tag.offering,
                    offeringName: getOfferingName(tag.offering),
                    operation: 'update',
                    run: () => dispatch(offeringTagUpdateThunk({ url: tag.url, data: { name: data.name } })).then(unwrapResult)
                })
            })

        addedOfferings.forEach(offering => {
            operations.push({
                offeringId: offering,
                offeringName: getOfferingName(offering),
                operation: 'create',
                run: () => dispatch(offeringTagCreateThunk({ offering, tag: data.tag, name: data.name })).then(unwrapResult)
            })
        })

        if (operations.length === 0) {
            actions.setSubmitting(false)
            return
        }

        // Initialize progress state
        const initialProgress = operations.map(op => ({
            offeringId: op.offeringId,
            offeringName: op.offeringName,
            operation: op.operation,
            status: 'pending' as const
        }))
        setProgress(initialProgress)

        try {
            const promises = operations.map(async (op) => {
                try {
                    const res = await op.run()
                    setProgress(prev => prev ? prev.map(item => 
                        item.offeringId === op.offeringId && item.operation === op.operation 
                            ? { ...item, status: 'success' } 
                            : item
                    ) : null)
                    return res
                } catch (err: any) {
                    const errMsg = extractError(err) || `Failed to ${op.operation} tag`
                    setProgress(prev => prev ? prev.map(item => 
                        item.offeringId === op.offeringId && item.operation === op.operation 
                            ? { ...item, status: 'failed', error: errMsg } 
                            : item
                    ) : null)
                    throw err
                }
            })

            const results = await Promise.allSettled(promises)
            const fulfilledCount = results.filter(r => r.status === 'fulfilled').length
            const rejectedCount = results.filter(r => r.status === 'rejected').length

            if (fulfilledCount > 0) {
                setIsSuccess(true)
                dispatch(offeringTagListThunk({}))
            }

            if (rejectedCount > 0) {
                setSubmitError(`${rejectedCount} operation${rejectedCount === 1 ? '' : 's'} failed. See details on the right.`)
            } else {
                setInitialValues({ offering: data.offerings[0] ?? '', offerings: data.offerings, name: data.name, tag: data.tag })
            }
        } catch (err: any) {
            Object.entries(getFieldErrors(data, err)).forEach(([key, value]) =>
                actions.setFieldError(key, value))
            setSubmitError(extractError(err))
        } finally {
            actions.setSubmitting(false)
        }
    }

    const handleSuccess = () => isSuccess ? 'Offering tag updated' : ''

    if (!hasEditPermission) return null

    return (
        <GoBack {...props}>
            <Box display="flex" flexDirection={{ xs: 'column', md: 'row' }} alignItems="flex-start">
                <Box style={{ flexGrow: 1, maxWidth: '100%' }}>
                    <FitContentPaper style={{ width: 'min(1180px, 100%)' }}>
                        <Box component="h5" mb={4}><IntlMessages id="offeringTag.edit.title" /></Box>
                        <FormOfferingTag
                            buttonLabelId="common.update"
                            api={api}
                            initialValues={initialValues}
                            onSubmitCb={handleSubmit}
                            isSuccess={isSuccess}
                            successCb={handleSuccess}
                            offerings={offerings}
                            availableTags={availableTags}
                            offeringTags={offeringTags}
                            disabledExcludeTagIds={groupTags.map(tag => tag.id)}
                            isBulkAssign
                            isEdit
                        />
                        {submitError && (
                            <Box marginTop={4}>
                                <Alert severity="error">{submitError}</Alert>
                            </Box>
                        )}
                    </FitContentPaper>
                </Box>
                {progress && (
                    <Box 
                        ml={{ xs: 0, md: 4 }} 
                        mt={{ xs: 4, md: 0 }} 
                        style={{ width: '100%', maxWidth: '400px', flexShrink: 0 }}
                    >
                        <BulkProgressList progress={progress} />
                    </Box>
                )}
            </Box>
        </GoBack>
    )
}

export default EditOfferingTag
